/**
 * Authentication Initialization Plugin
 *
 * Restores the user session on page reload by reading localStorage data
 * and attempting to restore the authenticated state.
 *
 * This plugin runs on the client-side to ensure:
 * - Valid sessions are restored without requiring re-login
 * - Expired tokens trigger automatic refresh attempts
 * - Invalid or expired sessions are properly cleaned up
 *
 * Flow:
 * 1. Read token_expires_at from localStorage
 * 2. If data exists, restore tokenExpiresAt to store
 * 3. If token expired, attempt to refresh
 * 4. Fetch current user to verify session validity
 * 5. On failure, clear auth state (middleware will redirect to login)
 *
 * Key scenarios handled:
 * - Valid session: restored, no login required
 * - Expired token with valid refresh token: refreshed, restored
 * - Both tokens expired: logged out, middleware redirects to login
 * - Cleared cookies/localStorage: remains logged out
 * - Network/API errors: cleaned up state, middleware redirects
 */

export default defineNuxtPlugin({
  name: 'auth-init',
  enforce: 'pre',
  // Ensure Pinia is initialized before this plugin runs
  dependsOn: ['pinia'],

  async setup(nuxtApp) {
    // Only run on client side
    if (!import.meta.client) {
      return
    }

    try {
      // Use $pinia from nuxtApp to ensure Pinia context is available
      const pinia = nuxtApp.$pinia
      const authStore = useAuthStore(pinia)

      // Attempt to restore the session
      // This will check localStorage, refresh token if needed, and fetch user
      // On failure, it clears state - the global middleware will handle redirects
      await authStore.restoreSession()
    } catch (error) {
      // Log unexpected errors but allow the app to continue
      // This ensures the app can start even if session restoration fails unexpectedly
      console.error('Auth initialization failed unexpectedly:', error)
    }
  },
})
